/**
 * Routing Hooks
 * Custom hooks for navigation and route utilities
 */

import { useNavigate, useParams as useRouterParams, useLocation } from 'react-router-dom'
import { RouteBuilder, RouteParams } from './types'
import { ROUTES } from './routes'

/**
 * Type-safe navigation hook
 * Usage:
 *   const navigate = useTypedNavigate()
 *   navigate(ROUTES.pm.programs.detail, { programId: '123' })
 */
export function useTypedNavigate() {
  const navigate = useNavigate()

  return (builder: RouteBuilder, params?: Record<string, string>) => {
    const path = builder.build(params)
    navigate(path)
  }
}

/**
 * Type-safe params hook
 * Extracts and types route parameters
 */
export function useParams<T extends RouteParams = RouteParams>(): T {
  return useRouterParams() as T
}

/**
 * Get current program context from route
 */
export function useProgramContext() {
  const params = useParams()
  const location = useLocation()

  return {
    programId: params.programId,
    inProgramContext: !!params.programId,
    currentPath: location.pathname,
  }
}

/**
 * Get breadcrumb trail for current route
 */
export function useBreadcrumbs() {
  const location = useLocation()
  const params = useParams()

  // Build breadcrumb trail from path
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const breadcrumbs: Array<{ label: string; path: string }> = []

  // Build up path segments
  let currentPath = ''
  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i]
    currentPath += `/${segment}`

    // Check if segment is a param (e.g., UUID or ID)
    const isParam = segment.match(/^[a-f0-9-]{36}$|^\d+$/)

    // Generate label
    let label = segment.charAt(0).toUpperCase() + segment.slice(1)
    if (isParam && params.programId === segment) {
      label = `Program ${segment.slice(0, 8)}...`
    }

    breadcrumbs.push({
      label,
      path: currentPath,
    })
  }

  return breadcrumbs
}

/**
 * Check if a route is currently active
 */
export function useIsRouteActive(builder: RouteBuilder, exact: boolean = false): boolean {
  const location = useLocation()

  if (exact) {
    return location.pathname === builder.path
  }

  return location.pathname.startsWith(builder.path)
}

/**
 * Get all routes for current user
 */
export function useUserRoutes() {
  // This would integrate with your auth system
  // For now, return PM routes as default
  return ROUTES.pm
}

/**
 * Navigate back with fallback
 */
export function useNavigateBack(fallbackRoute?: RouteBuilder) {
  const navigate = useNavigate()
  const typedNavigate = useTypedNavigate()

  return () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else if (fallbackRoute) {
      typedNavigate(fallbackRoute)
    } else {
      navigate('/dashboard')
    }
  }
}

